import { Autocomplete } from '@mui/joy';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import useSWR from 'swr';

import { axiosInstanceForTenantAPI } from '../../../../assets/utils/axiosInstance';
import { LinkedReportOption } from '../../../../interfaces/reportData';
import { setLinkedReportOptions } from '../../../../slices/reportDataSlice';
import { RootState } from '../../../../utils/store';
import { transformLinkedReports } from '../helper';

import { Option, Select } from '~/components/ui/select';

interface SelectLinkedReportProps {
  selected: Array<LinkedReportOption>;
  onChangeSelected: (value: Array<LinkedReportOption>) => void;
  disabledFlag: boolean | undefined;
}

const SelectLinkedReport = ({
  selected,
  onChangeSelected,
  disabledFlag,
}: SelectLinkedReportProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const intl = useIntl();
  const [fieldValue, setFieldValue] = useState('');
  const { referenceId: refId } = useSelector(
    (state: RootState) => state.reportData.report,
  );
  const linkedReportOptions = useSelector(
    (state: RootState) => state.reportData.linkedReportOptions,
  );

  const fetcher = (url: string): Promise<unknown> =>
    axiosInstanceForTenantAPI()
      .get(url)
      .then((res) => res.data);

  const { data } = useSWR(
    refId ? `/report-list?reference_id=${refId}` : '',
    fetcher,
  );

  useEffect(() => {
    if (data && Array.isArray(data)) {
      dispatch(setLinkedReportOptions(transformLinkedReports(data) ?? []));
    }
  }, [data, dispatch]);

  // update data from parent component
  const updateLinkedRepotList = (value: string | null) => {
    const newData = linkedReportOptions.find(
      (x) => x.formattedReportNumber === value,
    );
    if (newData) {
      onChangeSelected([...selected, newData]);
      setFieldValue('');
    }
  };
  // filter the reports that has already been selected.
  const filteredLinkReportOptions = linkedReportOptions
    .filter(
      (option) =>
        !selected.some(
          (l) => l.formattedReportNumber === option.formattedReportNumber,
        ),
    )
    .map((a) => a.formattedReportNumber);

  return (
    <div className='flex flex-col gap-2'>
      <Autocomplete
        size='lg'
        disabled={disabledFlag}
        value={fieldValue}
        options={filteredLinkReportOptions}
        onChange={(_, value) => {
          updateLinkedRepotList(value);
        }}
        forcePopupIcon={false}
        slotProps={{
          root: { ref: containerRef },
          listbox: {
            popperOptions: { strategy: 'absolute' },
            container: () => containerRef.current,
          },
        }}
        // placeholder={intl.formatMessage({
        //   defaultMessage: 'Enter Report reference',
        // })}
      />
      <div className='flex justify-end'>
        <Select
          size='sm'
          className='!bg-brand-primary-600 !text-white'
          disabled={disabledFlag}
          value='000'
          placeholder={intl.formatMessage({ defaultMessage: 'Report List' })}
          mountOptionsToRoot
        >
          <Option value='000' className='!hidden'>
            <FormattedMessage defaultMessage='Linked Reports' />
          </Option>
          {selected.map(({ referenceId, formattedReportNumber }) => {
            return (
              <Option
                value={referenceId}
                key={`${referenceId}-${formattedReportNumber}`}
              >
                {formattedReportNumber}
              </Option>
            );
          })}
        </Select>
      </div>
    </div>
  );
};

export default SelectLinkedReport;
